import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { isAccessErrorBody } from '../models/access-error.model';
import { AccessErrorDispatcher } from '../services/access-error/access-error-dispatcher.service';
import { BYPASS_ACCESS_ERROR_TOAST } from '../services/http/http-context';

/**
 * Outermost interceptor: catches 4xx/5xx responses that carry the BE
 * feature-access denial contract (see `access-error.model.ts` and the
 * error table in `crp-be/entitlements/gate.py`), hands the body to
 * `AccessErrorDispatcher` for interpretation + toast + CTA routing,
 * and re-throws the error unchanged so downstream chain (retry,
 * error-normalisation) still runs.
 *
 * Placement matters — this must sit **outside** `httpErrorInterceptor`,
 * because that one normalises the raw `HttpErrorResponse` into an
 * `ApiError` (dropping the `hint` and `feature_id` fields the
 * dispatcher needs). By the time an inner interceptor sees the
 * error, the wire body is gone.
 *
 * Opt-out per request via `httpOptions({ bypassAccessErrorToast: true })`:
 *   - Endpoints that render their own denial UI (the entitlement-
 *     overrides admin panel already handles 403 inline).
 *   - Silent background polls that shouldn't spam toasts.
 * See `http-context.ts` for the token doc.
 *
 * The interceptor never swallows errors — it just tees off a toast on
 * matching bodies. Callers still receive the original observable
 * failure so business logic can react (redirect, disable a button,
 * etc.).
 */
export const httpAccessErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const dispatcher = inject(AccessErrorDispatcher);

  return next(req).pipe(
    catchError(error => {
      if (req.context.get(BYPASS_ACCESS_ERROR_TOAST)) {
        return throwError(() => error);
      }

      // Only inspect HttpErrorResponse — RxTimeoutError, network
      // failures, and pre-request errors don't carry a body we can
      // interpret. Angular parses the body into `.error` when the
      // Content-Type is application/json.
      if (error instanceof HttpErrorResponse && isAccessErrorBody(error.error)) {
        dispatcher.dispatch(error.error);
      }

      return throwError(() => error);
    })
  );
};
